import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { StackedListField, StackedListItem } from './stacked-list.util';

@Component({
  selector: 'tcc-mono-stacked-list',
  templateUrl: './stacked-list.component.html',
  styleUrls: ['./stacked-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StackedListComponent {
  @Input() public items: any[];
  @Input() public selectedItem: any;
  @Input() public compareSelectedBy: string = 'id';
  @Input() public listItems: StackedListItem;
  @Input() public selectable: boolean = false;
  @Input() public loading: boolean;
  @Input() public clickable: boolean = true;
  @Input() public checkedList: any[] = [];
  private _fields: StackedListField[];
  @Input() public set fields(fields: StackedListField[]) {
    this._fields = fields;
  };

  @Input() public itemSize: number = 70;
  @Input() public minBufferPx: number = 1300;
  @Input() public maxBufferPx: number = 1600;
  @Input() public infiniteScroll: boolean = true;
  /** Optioneel: custom trackBy voor betere scroll-performance (bijv. (i, item) => item.dierLevensnummer). */
  @Input() public trackBy: (index: number, item: any) => any;

  public get fields(): StackedListField[] {
    return this._fields ?? this.listItems?.fields ?? [];
  }

  @Output() public itemClicked = new EventEmitter<any>();
  @Output() public loadData = new EventEmitter<any>();

  @ViewChild(CdkVirtualScrollViewport) private _viewport: CdkVirtualScrollViewport;

  /** Scroll naar boven (bijv. na filterwissel zodat virtual scroll niet de weg kwijt raakt). */
  public scrollToTop(): void {
    this._viewport?.scrollToIndex(0);
  }

  public isSelected(item: any): boolean {
    return item?.[this.compareSelectedBy] === this.selectedItem?.[this.compareSelectedBy];
  }

  public isChecked(item: any): boolean {
    const index = this.checkedList.findIndex(i => item[this.compareSelectedBy] === i?.[this.compareSelectedBy]);
    const val = index !== -1
    return val;
  }

  public itemClick = (item: any) => {
    this.selectedItem = item;
    this.itemClicked.emit(item);

    if (this.selectable) {
      this.check(item, null)
    }
  }

  public loadDataTrigger = ($event: any) => {

    this.loadData.emit($event);

  }

  public getProperty(item: any, field: StackedListField) {
    if (typeof field.property === 'string') {
      return item[field?.property];
    } else {
      return field.property(item);
    }
  }

  public getLink(item: any, routerUrl: string | Function) {
    if (typeof routerUrl === 'string') {
      return routerUrl;
    } else {
      return routerUrl(item);
    }
  }

  /** Arrow zodat *cdkVirtualFor bij aanroep de juiste `this` behoudt. */
  public trackByFn = (index: number, item: any) =>
    this.trackBy ? this.trackBy(index, item) : (item?.id != null ? item.id : index);

  public check(item: any, event: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    const index = this.checkedList.findIndex((i => item[this.compareSelectedBy] === i?.[this.compareSelectedBy]));

    if (index === -1) {
      this.checkedList.push(item);
    } else {
      this.checkedList.splice(index, 1);
    }
  }

  public isDisabled(item: any, listItem: StackedListItem): boolean {
    if (!listItem) {
      return false;
    }
    if (typeof listItem.disabled === 'boolean') {
      return listItem.disabled;
    }

    if (typeof listItem.disabled === 'function') {
      return listItem.disabled(item);
    }

    return false;
  }

  public selectAll(items: any[]): void {
    if (items.length) {
      this.checkedList.length = 0;

      items.forEach(item => {
        this.checkedList.push(item)
      });
    }
  }

  public hasAvatar(item: any): boolean {
    if (!this.listItems?.avatar) {
      return false;
    }
    if (typeof this.listItems.avatar === 'string') {
      return !!this.listItems.avatar;
    }
    const avatarValue = this.listItems.avatar(item);
    return !!avatarValue;
  }
}
